/**
 * PlanSelection.tsx — pick a medication + plan term after an eligible result.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C2.1 (plan builder redo);
 * recommendation-aware UX Wave 4 §2 (2026-07-21).
 *
 * WHY: this is a POST-ELIGIBILITY, authed surface, so medication (POM) names
 * and prices are allowed here (R28 applies to PUBLIC/pre-eligibility screens
 * only). C2.1 replaces the single hardcoded quote (`productKey='starter'`)
 * with the clinic's OPTIONS endpoint: the patient first picks a medication
 * (every configured option renders as a card — name, form, cold-chain note),
 * then a term (1/3/6/12 months) for that option. The option flagged
 * `recommended` by the server is preselected — `recommended` is a BOOLEAN on
 * the OPTION (an earlier build misread it as a months value); the default
 * TERM is a UI decision: the middle 3-month term when present, else the first
 * plan. Prices are integer PENCE — a 0 renders as the launch placeholder (see
 * lib/format). The options fetch is a critical call, so a failure shows a
 * rendered, retryable error (TA-06).
 *
 * CLINICIAN RECOMMENDATIONS (Wave 4 §2 + patient-safe read): when
 * `assessmentId` is given, the PATIENT-safe recommendations endpoint is used
 * (never the staff GET) — guests send the checkout grant on a dedicated
 * guest path (gateway stamps the checkout principal; the PATIENT URL keeps
 * its bearer). Outcomes:
 *   - `recommendation: null` → Route 1 full catalogue (unchanged).
 *   - `productKeys` present → filter + two-step chooser (Route 2).
 *   - read failure / keys match no options → ErrorState; NEVER the full list.
 * The two reads and that rule live in `planScreenRead.ts` (2026-09-25), ONE
 * copy shared with CheckoutFlow, which starts them early on the pre-plan
 * loader's lane and hands them in (`earlyRead`) so its set loading time runs
 * alongside them — this screen then opens on their answer with no skeleton
 * frame, and still reads for itself everywhere else and on every retry.
 *
 * DISCOUNTED TERM PRICING (CTO model): the 1-month option's monthlyPence is
 * the per-month REFERENCE; longer terms are configured cheaper per month. Each
 * term row renders via the SHARED {@link PlanTermCard} — the same renderer the
 * admin plan-builder preview uses, so the two can never drift — with the
 * arithmetic in {@link termPricing} (integer pence, no invented rounding).
 *
 * THE TERM IS THE PATIENT'S CHOICE AGAIN (2026-09-04, CTO: "let them pick the
 * plan they want, because sometimes people might want to go for longer term
 * commitments… It shouldn't get defaulted to 3. Always keep the 3rd month
 * selected by default in the UI, but they should have the ability to switch
 * between anything that they want"). This REVERSES the 2026-08-28 lock's first
 * half only — see {@link DEFAULT_MONTHS} for what came back, and for the half
 * that did NOT change: the patient's one-press way out, which is the reason a
 * multi-month commitment is defensible at all and is now the reason a 1-month
 * plan has to state that it ends.
 *
 * ⚠️ THE WEIGHT PHOTO IS NOT ASKED FOR ON THIS STEP AT ALL (2026-09-04, CTO:
 * "remove the photo option even from the plan builder screen. It's not needed,
 * and right now it still guards Continue to delivery"). That REVERSES a
 * documented decision, so the decision is recorded here rather than deleted with
 * the code:
 *
 *   · 2026-08-27, CTO wave 3 — the ask MOVED here from q14, on reasoning that
 *     was sound: q14 came mid-questionnaire, before the patient had decided they
 *     wanted treatment, so most chose "I'll add it later" and clinicians
 *     approved with nothing in front of them. Plan selection is the moment the
 *     same patient has decided they DO want this.
 *   · 2026-08-30 — the gate was made unconditional of everyone ("they shouldn't
 *     be able to go beyond that point without doing that"), because with a defer
 *     link beside the ask, weeks of real submissions produced almost no photos.
 *   · 2026-09-04 — what settled it was the funnel: ~25 leads/week down to ~2. A
 *     mandatory image upfront works exactly as designed and empties the top of
 *     the funnel. clinic #166 replaced q14 with a self-certification DECLARATION
 *     and orders#197 dropped the checkout mandate — but this screen still
 *     computed a gate of its own, so the single bottleneck the whole
 *     self-certification programme exists to remove was still live on the one
 *     screen where a patient commits money.
 *
 * WHAT REPLACES IT IS A WINDOW, NOT A GATE, and it already exists. Evidence can
 * be added by the patient, an admin or a clinician at any time up to the second
 * dispense: `patients/WeightPhotoCard` is the patient's lane, mounted on the
 * thank-you page and the dashboard (CTO: "they can upload it once they are
 * inside the profile"); the clinician/admin evidence card is the staff lane; and
 * orders-service refuses a dispense only where a RECORDED request went
 * unanswered. R41 is untouched by this — the real control was never on this
 * screen. ⚠️ DO NOT REBUILD THE ASK HERE. A conversion-step upload has now been
 * tried twice and cost the funnel both times; CLAUDE.md carries the standing
 * rule ("do not build a universal mandatory photo step anywhere").
 */
import { dropOffPagePath } from '../journey/dropOffReasons';
import { NoChargeLine } from '../journey/NoChargeLine';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlanOption, PricePlan } from '../api/types';
import { withBasename } from '../config';
import { formatPence } from '../lib/format';
import { termPricing, type TermPricing } from '../lib/termPricing';
import { Badge, Button, CheckIcon, ErrorState, Help, Title, TrustStrip } from '../ui/kit';
import { SkeletonCard } from '../ui/Skeleton';
import {
  CommitmentTerms,
  CommitmentTermsDetail,
  CommitmentTermsTick,
  commitmentPanelStyle,
} from './CommitmentTerms';
import { DELIVERY_SUMMARY_ROW } from './deliveryCopy';
import {
  orderSummaryCardStyle,
  SummaryEyebrow,
  SummaryFigure,
  SummaryPlanName,
  SummaryRow,
  summaryRowsStyle,
  termLabel,
  termTotalLabel,
} from './OrderSummary';
import { earlyReadFor, readPlanScreen, type EarlyPlanScreenRead, type PlanScreenRead } from './planScreenRead';
import { PLAN_STEP_COMMITMENT, PLAN_STEP_TREATMENT } from './planStepCopy';
import { PlanTermCard } from './PlanTermCard';
import { TreatmentFit } from './TreatmentFit';
import { TreatmentFormIcon } from './TreatmentFormIcon';
import { WeightProjection } from './WeightProjection';

const FALLBACK_TERMS = [1, 3, 6, 12];

/**
 * THE TERM THE SCREEN OPENS ON — three months, and nothing more than that
 * (CTO, 2026-09-04: "always keep the 3rd month selected by default in the UI,
 * but they should have the ability to switch between anything that they want").
 *
 * A DEFAULT, NOT A LOCK, AND THE DIFFERENCE IS THE WHOLE POINT OF THIS FILE'S
 * HISTORY. From 2026-08-28 this was `LOCKED_MONTHS` and the only term anyone
 * could buy; both term controls were deleted, on the reasoning that four
 * commitments weighed at the moment the patient meets the price is a decision
 * taken at the worst possible time. That call is REVERSED: patients do want
 * longer terms, and a clinic that prices four of them was offering one. What
 * came back is the 2026-08-19 arrangement — {@link TermRadioGroup} above the
 * chart, the priced commitment cards at step 2, one `months` between them —
 * with three months preselected, which is what the lock had made mandatory and
 * what the UI had preselected before it anyway.
 *
 * ⚠️ THE OTHER HALF OF THE LOCK DID NOT REVERSE, AND MUST NOT. The locked term
 * was only ever defensible because stopping is genuinely one press away, from
 * the patient's own account, with nothing owed for months they have not had.
 * That control is live (orders-service `POST /v1/subscriptions/:id/cancel`,
 * surfaced on the patient dashboard) and {@link CommitmentTerms} states it here
 * at the moment of the decision. Restoring CHOICE removes no part of that: a
 * patient choosing six or twelve months needs the exit more than one accepting
 * three, not less. If the exit is ever taken away, the multi-month terms go
 * with it — that is a consumer-law position, not a conversion tweak.
 *
 * ⚠️ AND THE 1-MONTH TERM CARRIES A DISCLOSURE, BECAUSE IT IS THE ONE THE LOCK
 * NAMED AS THE REAL HARM. It never renews, so it manufactures a "come back and
 * pay again" event every month, and the patients who forget — or whose email
 * lands in spam — simply stop treatment. The CTO has asked for the term back,
 * which is their call to make; what this file owes the patient in return is
 * that the consequence is legible AT THE POINT OF CHOICE rather than discovered
 * a month later. See `termEndNote`. It states what happens; it does not
 * block, warn or argue for a longer term.
 *
 * ⚠️ IT IS ALSO A DEFAULT, NOT AN ASSUMPTION. A catalogue option that does not
 * price a 3-month term still has to be buyable — see {@link defaultMonths},
 * which falls back rather than opening on a term nobody sells.
 *
 * The 2026-08-14 two-badge merchandising ("Most popular" / "Best value" on the
 * term cards) stays DELETED, and its reason has changed rather than expired.
 * The lock's reason — nothing left to compare — is gone. What stands now is
 * that both pills argue for a particular commitment, on a screen that has just
 * been handed back the patient's own choice, and one of them was a social-proof
 * claim backed by a constant in this file rather than by any order data.
 * Putting them back is a merchandising decision for the CTO to take on its own
 * merits; it is not a side effect of restoring the control.
 */
const DEFAULT_MONTHS = 3;

/**
 * The ids that tie the plan builder's TWO term controls to one another.
 *
 * Static rather than generated: this screen renders exactly once per page (it
 * is a checkout step, not a list item), and the file already fixes
 * `continue-blocked-reason` the same way. A stable id is also what lets a spec
 * assert the link is really wired rather than that some description exists.
 */
const TERM_CONTROL_LABEL_ID = 'plan-term-control-label';
const TERM_CONTROL_HINT_ID = 'plan-term-control-hint';
const TERM_CARDS_SAME_CHOICE_ID = 'plan-term-cards-same-choice';

/** Why Continue is off while the commitment tick is unticked — see `blockedReasonLine`. */
const CONTINUE_BLOCKED_REASON = 'Tick the box to agree to your plan and continue.';

/**
 * The cheapest monthly price across an option's terms (2026-07-20, Ashley's
 * feedback; the Wave-4 "from £X/month" card headline uses it too): a
 * discounted longer term already carries the lower `monthlyPence` value
 * server-side, so the minimum across `plans` is exactly the right
 * "starting from" figure — no separate discount calc needed here.
 */
function startingFromPence(option: PlanOption): number {
  if (option.plans.length === 0) return 0;
  return Math.min(...option.plans.map((p) => p.monthlyPence));
}

export interface PlanChoice {
  productKey: string;
  months: number;
  medicationName: string;
  monthlyPence: number;
}

/**
 * A month's price expressed PER DAY (2026-08-14, Ashley Haveloch-Jones).
 *
 * "£169.38 a month" is a bill; "£5.57 a day" is a coffee. Same money, and the
 * patient does the comparison we want them to make — against daily spending
 * rather than against rent. The monthly figure stays on the card underneath: the
 * per-day framing must ILLUSTRATE the price, never replace it, because the
 * monthly amount is what actually leaves their account.
 *
 * 30.44 = 365.25 / 12, so the figure does not drift between a 28- and a 31-day
 * month. Display only — never fed back into anything that charges.
 */
function perDayPence(monthlyPence: number): number {
  return Math.round(monthlyPence / 30.44);
}

/**
 * The TERM an option OPENS on: {@link DEFAULT_MONTHS}, or the option's first
 * priced term when the catalogue does not sell a 3-month plan for it.
 *
 * The fallback is the whole point of having a function rather than a constant,
 * and it survives the lock's reversal unchanged. Returning 3 for an option
 * priced only at 6 and 12 months would preselect a term the catalogue cannot
 * quote and checkout cannot charge — a 0-pence price block, and a patient
 * stranded on a plan nobody sells until they notice the chip they are standing
 * on is not in the row. Three months is what we open on where it is real, not a
 * claim about what every clinic sells.
 */
function defaultMonths(option: PlanOption | undefined): number {
  if (!option) return DEFAULT_MONTHS;
  if (option.plans.some((p) => p.months === DEFAULT_MONTHS)) return DEFAULT_MONTHS;
  return option.plans[0]?.months ?? DEFAULT_MONTHS;
}

/**
 * The plan a patient already chose, handed back when they return here from the
 * Delivery & payment step's **Edit plan** (CTO 2026-09-24). Identity only — the
 * product and the term. Never a price: the price on this screen is the live
 * catalogue's, exactly as on a first visit.
 */
export interface InitialPlanChoice {
  productKey: string;
  months: number;
}

/**
 * WHAT THE SCREEN OPENS ON — the recommended option at {@link defaultMonths},
 * or, on an Edit plan round trip, the patient's own current choice.
 *
 * THE RETURNING CHOICE FOLLOWS `chooseOption`'S RULE, NOT A LOOSER ONE: the term
 * is kept only where the option still sells it, and otherwise re-derives from
 * {@link defaultMonths} — so a return can never land the patient on a term the
 * catalogue cannot quote. And a returning product that is no longer in the pool
 * (withdrawn, or filtered out by a clinician's recommendation set) is simply not
 * preselected: the screen opens as it would for anyone, rather than on a card
 * that is not on it.
 *
 * WHY HERE AND NOT BY CALLING `chooseOption`/`chooseTerm`: those are render-time
 * handlers that exist only once options have loaded, and this is the moment the
 * options land — the same three initial-state writes that already set the
 * recommended preselection. The one thing those handlers add, clearing the
 * commitment tick, is already true: PlanSelection REMOUNTS for the round trip,
 * so the tick starts false. Consent given for the plan being edited must not
 * carry to whatever the patient leaves with, and it cannot.
 */
function openingSelection(
  pool: PlanOption[],
  initialChoice: InitialPlanChoice | undefined,
): { productKey: string | null; months: number } {
  const returning = initialChoice ? pool.find((o) => o.productKey === initialChoice.productKey) : undefined;
  if (returning && initialChoice) {
    const keepsTerm = returning.plans.some((p) => p.months === initialChoice.months);
    return { productKey: returning.productKey, months: keepsTerm ? initialChoice.months : defaultMonths(returning) };
  }
  const preselected = pool.find((o) => o.recommended) ?? pool[0];
  return { productKey: preselected?.productKey ?? null, months: defaultMonths(preselected) };
}

/** What the screen shows for one answer of its reads — see {@link planScreenView}. */
interface PlanScreenView {
  state: 'ready' | 'error' | 'recs_error';
  options: PlanOption[] | null;
  recommendedKeys: string[] | null;
  productKey: string | null;
  months: number;
}

/**
 * The screen for one answer of its reads (`planScreenRead.ts`): the options
 * with {@link openingSelection} applied, or which error. PURE, because it runs
 * in two places that must agree — the mount that OPENS on an answer that has
 * already landed (CheckoutFlow's early read, so no skeleton frame paints
 * between its loader and this screen) and every answer that lands later.
 *
 * Only the VISIBLE (clinician-recommended) options can be returned to — a plan
 * outside the set is never preselected onto a screen that hides it.
 */
function planScreenView(read: PlanScreenRead, returning: InitialPlanChoice | undefined): PlanScreenView {
  if (read.kind !== 'ready') {
    return { state: read.kind, options: null, recommendedKeys: null, productKey: null, months: DEFAULT_MONTHS };
  }
  const keys = read.recommendedKeys;
  const pool = keys ? read.options.filter((o) => keys.includes(o.productKey)) : read.options;
  let opening;
  try {
    opening = openingSelection(pool, returning);
  } catch {
    // A malformed option (no terms list) is the failed read it always was —
    // the retryable error, never a crashed screen.
    return { state: 'error', options: null, recommendedKeys: null, productKey: null, months: DEFAULT_MONTHS };
  }
  return {
    state: 'ready',
    options: read.options,
    recommendedKeys: keys,
    productKey: opening.productKey,
    months: opening.months,
  };
}

/** Medication + plan-term selector driven by the clinic's plan options. */
export function PlanSelection({
  offerDropOffReason,
  clinicSlug,
  assessmentId,
  checkoutToken,
  startKg,
  bmi,
  initialChoice,
  earlyRead,
  onSelect,
}: {
  clinicSlug: string;
  /** Wave 4 §2: enables the clinician-recommendation read for this assessment. */
  /**
   * T-2 — the plan-link lane only (an approved, unpaid patient). Offers the
   * quiet "what's holding you back?" line under the CTA ({@link HoldingBackLink}).
   */
  offerDropOffReason?: boolean;
  assessmentId?: string;
  /** Guest checkout grant — plan options without PATIENT session. */
  checkoutToken?: string;
  /**
   * The patient's own recorded starting weight, in kilograms, as captured at the
   * top of the journey. Turns the projection's percentages into kilograms for
   * THIS person — a unit conversion of a server-stated figure, not a new claim.
   *
   * Absent is a normal state (a cold resume that never re-hydrated the capture
   * step), and it means the band shows percentages. NEVER substitute an average
   * body weight to fill the gap: that would put an invented kilogram figure in
   * front of someone deciding whether to start a prescription medicine.
   */
  startKg?: number;
  /**
   * The patient's BMI as the CLINIC calculated it. Absent ⇒ the eligibility
   * sentence is omitted. Never recomputed here from height and weight — the
   * clinic decides eligibility, and a second derivation is a second answer.
   */
  bmi?: number;
  /**
   * Edit plan (CTO 2026-09-24): the patient's current choice, preselected on
   * return from the Delivery & payment step. Absent on a first visit. See
   * {@link openingSelection} for the rule it is applied through.
   */
  initialChoice?: InitialPlanChoice;
  /**
   * THE PRE-PLAN LOADER'S EARLY READ (release review, 2026-09-25 —
   * `planScreenRead.ts`): this screen's reads, started by CheckoutFlow the
   * moment its restore landed here, so its loader's set loading time runs
   * ALONGSIDE them rather than before them. Taken by this mount only when it
   * was made for this clinic, assessment and grant; a retry reads afresh.
   * Absent everywhere else: the screen reads for itself on mount, as it always
   * has.
   */
  earlyRead?: EarlyPlanScreenRead;
  onSelect: (choice: PlanChoice) => void;
}) {
  // Primitives, not the object: a caller that rebuilt `initialChoice` on every
  // render would otherwise re-fire the options read on every render.
  const initialProductKey = initialChoice?.productKey;
  const initialMonths = initialChoice?.months;
  /**
   * The early read, held for THIS mount (never a dependency of `load`, so the
   * parent letting go of it re-reads nothing). Cleared by a retry.
   */
  const early = useRef(earlyReadFor(earlyRead, clinicSlug, assessmentId, checkoutToken));
  /**
   * An early read that has ALREADY LANDED (CheckoutFlow's loader waited for it)
   * is what the screen opens on: its first paint is the plan, or its error,
   * never a skeleton frame. Computed once, from the same pure view every later
   * answer goes through.
   */
  const [opening] = useState<PlanScreenView | null>(() => {
    const landed = early.current?.settled;
    return landed ? planScreenView(landed, initialChoice) : null;
  });
  const [options, setOptions] = useState<PlanOption[] | null>(opening?.options ?? null);
  const [recommendedKeys, setRecommendedKeys] = useState<string[] | null>(opening?.recommendedKeys ?? null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'recs_error'>(opening?.state ?? 'loading');
  const [productKey, setProductKey] = useState<string | null>(opening?.productKey ?? null);
  const [months, setMonths] = useState<number>(opening?.months ?? DEFAULT_MONTHS);
  // Two-step flow (recommendation mode only): medication first, then the term.
  const [step, setStep] = useState<'medication' | 'term'>('medication');
  // Terms consent is DELIBERATELY not persisted and resets whenever the FIGURE
  // changes — which since the term lock means whenever the medication changes
  // (see `chooseOption`). The patient agreed to a specific commitment at a
  // specific price, so carrying that tick onto a different one would be
  // recording consent they never gave.
  const [termsAccepted, setTermsAccepted] = useState(false);

  /* The rail's scrolling half, measured (see the ⚠️ note on the rail's JSX).
     Called here, above every early return, because it is a hook. */
  const railScroll = useRailOverflow();
  /* The 20rem rail (880–1199px), where the pinned foot has no room for the
     trust chips — see `useNarrowPlanRail`. Also a hook, so also up here. */
  const narrowRail = useNarrowPlanRail();

  /**
   * Puts one answer of the reads on screen ({@link planScreenView}). An answer
   * equal to what is already showing changes nothing (React keeps identical
   * state), which is what makes a second pass over a LANDED early read — the
   * development StrictMode re-run, a `load` re-created by its dependencies —
   * harmless.
   */
  const applyRead = useCallback((read: PlanScreenRead, returning: InitialPlanChoice | undefined) => {
    const view = planScreenView(read, returning);
    setOptions(view.options);
    setRecommendedKeys(view.recommendedKeys);
    if (view.state === 'ready') {
      setProductKey(view.productKey);
      setMonths(view.months);
      setStep('medication');
    }
    setState(view.state);
  }, []);

  const load = useCallback(() => {
    const returning =
      initialProductKey !== undefined && initialMonths !== undefined
        ? { productKey: initialProductKey, months: initialMonths }
        : undefined;
    const handed = early.current;
    // Landed already: straight onto the screen, no loading state in between.
    if (handed?.settled) {
      applyRead(handed.settled, returning);
      return;
    }
    setState('loading');
    // In flight (CheckoutFlow started it): the same read, never a second one.
    const pending = handed ? handed.result : readPlanScreen(clinicSlug, assessmentId, checkoutToken);
    void pending.then((read) => applyRead(read, returning));
  }, [clinicSlug, assessmentId, checkoutToken, initialProductKey, initialMonths, applyRead]);

  /** Try again: always a FRESH read — an early read that failed is never replayed. */
  const retry = useCallback(() => {
    early.current = null;
    load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  if (state === 'loading') {
    // A layout-matching skeleton (the coming screen is a stack of option/term
    // cards) instead of a bare spinner — no jump when the options land (E3 #4).
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto', display: 'grid', gap: 'var(--gps-s-3)' }}>
        <SkeletonCard lines={2} label="Fetching your plan options…" />
        <SkeletonCard lines={3} />
      </div>
    );
  }
  if (state === 'recs_error') {
    return (
      <ErrorState
        message="Sorry — we could not load your clinician's recommended treatments. Please try again. If this keeps happening, contact support — we will not show the full medication list."
        onRetry={retry}
      />
    );
  }
  if (state === 'error' || !options || options.length === 0) {
    return <ErrorState message="Sorry — we could not load your plan options." onRetry={retry} />;
  }

  const unordered = recommendedKeys ? options.filter((o) => recommendedKeys.includes(o.productKey)) : options;
  // ORDERED BY EXPECTED RESULT, STRONGEST FIRST (2026-08-14, CTO): the patient
  // reads the most effective option first and every card below it is a step
  // down, so the list itself carries the comparison. Options with NO approved
  // figure sort last rather than to the top — an unknown is not a zero, but it
  // also cannot claim a position it has not earned. Ties keep their original
  // server order (`sort` is stable), so the catalogue's own sequence still shows
  // through where two treatments share a figure.
  //
  // NOTE this is presentation only. It does NOT touch the clinician's
  // recommendation — that stays on whichever card carries `recommended`, and the
  // preselection still follows it. Ordering by potency must never be mistaken
  // for advising the strongest drug: suitability is the clinician's call, and
  // the approval gate is what actually decides.
  const visibleOptions = [...unordered].sort(
    (a, b) => (b.expectedWeightLossPercent ?? -1) - (a.expectedWeightLossPercent ?? -1),
  );
  const option = visibleOptions.find((o) => o.productKey === productKey) ?? visibleOptions[0];
  const plans = option.plans.length > 0 ? option.plans : FALLBACK_TERMS.map((m) => ({ months: m, monthlyPence: 0 }));
  const selectedPlan = plans.find((p) => p.months === months) ?? plans[0];
  // ⚠️ PRICING IS COMPUTED OVER THE WHOLE TERM LADDER, NOT OVER THE ONE TERM WE
  // SELL. `termPricing` measures each term against the 1-month per-month
  // reference — that difference IS the saving the order summary shows. Narrow
  // `plans` to the locked term to "simplify" this and every saving silently
  // becomes zero, because there is no longer anything to be cheaper than.
  const pricing = termPricing(plans);
  const selectedPricing = pricing.find((p) => p.months === selectedPlan.months) ?? pricing[0];

  // The strongest option, if any figure is published at all. Keyed by productKey
  // rather than index so it survives any later re-ordering.
  const mostEffectiveKey =
    typeof visibleOptions[0]?.expectedWeightLossPercent === 'number' ? visibleOptions[0].productKey : null;

  /**
   * The ONE way the term changes, shared by the term control above the chart
   * and the commitment cards below (2026-08-17; both controls again since
   * 2026-08-19; both controls AGAIN since 2026-09-04, and the CLAUDE.md rule
   * that says so never came down in between).
   *
   * WHY a shared handler rather than two `onClick`s that happen to do the same
   * thing: the term change has a second, load-bearing half — it CLEARS the terms
   * tick, because the patient agreed to a specific commitment at a specific
   * figure and that consent does not carry to a different one. Two call sites
   * would work on the day and drift the first time one of them is edited, and
   * the failure mode is silent: a recorded agreement to a term nobody agreed to,
   * about money.
   *
   * It is also the reason there is ONE `months`, not one per control. A second
   * piece of state would let the chart show a projection for a term the patient
   * is not buying — and the patient would have no way to tell which of the two
   * numbers on screen was the one they were about to be charged for.
   *
   * ⚠️ IT IS THE FILE'S ONLY WRITER OF `months`, INCLUDING FROM
   * {@link chooseOption}. Everything above holds only while that is true, so a
   * new control (or a "just move the chart" tweak) reaches for this and never
   * `setMonths`.
   */
  const chooseTerm = (next: number) => {
    setMonths(next);
    setTermsAccepted(false);
  };

  /**
   * Switching medication keeps the patient's chosen term when the new option
   * sells it, and re-derives from {@link defaultMonths} when it does not.
   *
   * KEEPING IT IS THE POINT (2026-09-04). The lock made this unconditional
   * because there was no patient pick to keep; there is again, and throwing away
   * a deliberate "12 months" because somebody compared two treatments would be
   * the screen overruling the choice it just handed back. We cannot tell a term
   * the patient CHOSE from one they merely landed on, and between those two
   * readings the respectful one is to keep it.
   *
   * THE TICK CLEARS EITHER WAY, and that is not belt-and-braces. The FIGURE
   * changed even when the term did not — a different treatment at a different
   * monthly price — so consent to the old commitment does not carry. The
   * re-derive path goes through `chooseTerm` (which clears it as well); this
   * line covers the path where the term stood still.
   */
  function chooseOption(next: PlanOption) {
    setProductKey(next.productKey);
    if (!next.plans.some((p) => p.months === months)) chooseTerm(defaultMonths(next));
    setTermsAccepted(false);
  }

  const medicationCards = visibleOptions.map((o) => {
    return (
      <MedicationCard
        key={o.productKey}
        option={o}
        selected={o.productKey === option.productKey}
        fromPence={startingFromPence(o)}
        mostEffective={o.productKey === mostEffectiveKey}
        onClick={() => chooseOption(o)}
      />
    );
  });

  /**
   * The term control that sits ABOVE the projection chart (2026-08-19, CTO:
   * "the controller for 1 month, 3 months, 6 months, and 12 months should be on
   * top of the chart... When you select one from there, it should pre-populate
   * that to question 2, and then from there, if they want, they can change it
   * again").
   *
   * The pre-population and the "change it again" are the same fact stated from
   * two ends: there is one `months`, both controls write it through
   * {@link chooseTerm}, so arriving at step 2 the patient finds their choice
   * already made, and changing it there moves this control back. Nothing
   * synchronises anything — there is nothing to synchronise.
   *
   * It offers the PURCHASABLE terms (`plans`), not the projected ones, and not a
   * hard-coded [1, 3, 6, 12]. contracts `PlanMonths` accepts those four and this
   * catalogue currently prices all four, but a clinic that stops selling twelve
   * must not show a 12 chip — that is a term checkout cannot quote and a
   * 0-pence price block. Conversely, a term the catalogue prices but has no
   * curve for is still a term someone may buy: the chart goes quiet for it (see
   * WeightProjection), the control does not.
   */
  const termControl = (
    <TermRadioGroup
      terms={plans.map((p) => p.months)}
      months={selectedPlan.months}
      onChoose={chooseTerm}
    />
  );

  const termCards = plans.map((plan, index) => (
    <TermCard
      key={plan.months}
      plan={plan}
      pricing={pricing[index]}
      selected={plan.months === selectedPlan.months}
      onClick={() => chooseTerm(plan.months)}
    />
  ));

  /**
   * The commitment cards as ONE named set — rendered on both routes.
   *
   * ⚠️ TWO CONTROLS, EXACTLY ONE RADIOGROUP (2026-08-19). This row and the term
   * control above the chart write the same `months`, so only one of them may
   * announce itself as THE radiogroup for it. Two radiogroups would tell a
   * screen-reader user there are two mutually-exclusive choices to make on this
   * page, when there is one choice offered twice — and they would have no way
   * to know that answering the first had already answered the second.
   *
   * THE CHIPS ABOVE TAKE THE RADIOGROUP; THIS ROW STAYS A LABELLED `group`.
   * `radiogroup` REQUIRES owned `role="radio"` children, and these cards are
   * `aria-pressed` toggle buttons matched deliberately to the treatment cards
   * above them — so satisfying the container would mean re-roling the single
   * most consequential control on the journey, the one that commits a patient
   * to a plan and a price, for a container's benefit. The chips carry no such
   * baggage: purpose-built, one line each, nothing but the term, and able to
   * honour everything radio semantics promise including arrow-key traversal.
   * `group` names this set without touching what its buttons say they are.
   *
   * SAME NAME, PLUS A STATED LINK. Both controls carry the accessible name
   * "How long to commit for", and on the single-page route this row also points
   * at a visually-hidden sentence saying it is that same choice restated with
   * prices. Matching names alone would leave a screen-reader user to INFER the
   * two are one; the sentence tells them, which is the difference between
   * meeting a choice twice and meeting two choices.
   *
   * `describedBy` is a parameter because the recommendation route has no chart
   * and no chips above — there is nothing there for this row to be "the same
   * as", and pointing at a sentence about a control that is not on screen would
   * be worse than saying nothing.
   */
  const termGrid = (describedBy?: string) => (
    <div
      role="group"
      aria-label="How long to commit for"
      aria-describedby={describedBy}
      className="gps-term-grid"
    >
      {termCards}
    </div>
  );

  /**
   * THE 1-MONTH TERM'S NON-RENEWAL, AT THE POINT OF CHOICE — one line, directly
   * under the priced cards, shown only while a one-month plan is selected.
   *
   * ⚠️ ONE MONTH: THE NON-RENEWAL, AT THE POINT OF CHOICE (2026-09-04). A
   * 1-month plan ENDS after its month — one-month plans deliberately do not
   * auto-renew (see `patient-portal/ResubscribeCard`, which exists because of
   * it). This is the harm the lock named when it deleted the term: the patient
   * who expected treatment to continue, and finds a month later that it quietly
   * did not. The minimum this screen owes in return is that the consequence is
   * legible BEFORE the money, not discovered afterwards. It STATES, and does
   * nothing else — no block, no warning tone, no comparison with the longer
   * terms, no nudge; and it promises no reminder email (none is built — see the
   * DMCCA note in {@link CommitmentTerms}) and no self-serve re-subscribe card
   * (the server offers it only to some patients).
   *
   * MULTI-MONTH: NOTHING HERE ANY MORE (2026-09-26, the CTO: "we are saying the
   * same thing over and over in different places; reduce the text"). This line
   * used to restate the stop point ("You can stop future months at any time
   * from your account — you only ever pay for the months you've had") a few
   * centimetres above the agreement panel's own. ⚠️ And that promise was FALSE:
   * orders-service bills every month of the term after a patient's "Stop future
   * months" (`SubscriptionsService.cancel()` honours the term). The panel's
   * point now says so — "Your plan runs for all N months — stopping from your
   * account doesn't end it early" — see CommitmentTerms' `CommitmentTermsDetail`.
   */
  const termEndNote =
    selectedPlan.months === 1 ? <p style={termEndNoteStyle}>This plan lasts one month and doesn&rsquo;t renew.</p> : null;

  /* THE WHOLE GATE IN ONE PANEL — what the recommendation route and every
     non-rail surface render. The two-column route below takes the same content
     in two pieces (`railAgreement` / `railTick`) so the tick can be pinned with
     the button; both are the SAME component's halves, so the contract a patient
     ticks cannot differ between the two routes. */
  const termsGate =
    selectedPlan.monthlyPence > 0 ? (
      <CommitmentTerms
        months={selectedPlan.months}
        monthlyPence={selectedPlan.monthlyPence}
        totalPence={selectedPricing.actualPence}
        accepted={termsAccepted}
        onChange={setTermsAccepted}
      />
    ) : null;

  /* The bullets, framed in the shared panel style — the part of the rail that
     SCROLLS. Its gap above is `.gps-plan-rail-agreement`'s (tokens.css): 16px
     under the summary on a phone, where the two are stacked in page flow; 2px
     at ≥880px, where the head's and the scrolling half's own 5px pads already
     part them and every pixel is the reading window's (review, 2026-09-26). */
  const railAgreement =
    selectedPlan.monthlyPence > 0 ? (
      <div className="gps-jux-panel gps-plan-rail-agreement" style={commitmentPanelStyle}>
        <CommitmentTermsDetail
          months={selectedPlan.months}
          monthlyPence={selectedPlan.monthlyPence}
          totalPence={selectedPricing.actualPence}
        />
      </div>
    ) : null;

  /* The tick — pinned with the CTA it gates, never left behind in the scrolling
     half (see the ⚠️ in CommitmentTerms' header). */
  const railTick =
    selectedPlan.monthlyPence > 0 ? (
      <CommitmentTermsTick
        months={selectedPlan.months}
        accepted={termsAccepted}
        onChange={setTermsAccepted}
      />
    ) : null;

  // The gate only bites when there is a real price. At the 0-pence launch
  // placeholder there is no commitment to agree to, so requiring a tick would
  // block checkout on a promise that does not exist yet.
  const termsBlocked = termsGate !== null && !termsAccepted;

  /**
   * ⚠️ ONE GATE ON THIS STEP, AND IT IS THE COMMITMENT TICK (2026-09-04).
   *
   * The photo gate that used to sit beside it is GONE — see the ⚠️ block in the
   * file header for the decision it reverses and why. What is worth keeping in
   * mind at the point the next gate is proposed: this line is on the money path.
   * Anything ANDed into it is something a patient who has decided to buy cannot
   * get past, on the screen the whole funnel narrows to. The commitment tick
   * earns that because the thing being agreed to IS the purchase; an upload of
   * evidence that a clinician can chase later does not, and did not.
   */
  const continueBlocked = termsBlocked;

  /**
   * The line under the CTA that says why it is off — in TEXT, tied to the button
   * by `aria-describedby`, never colour or a bare `disabled` attribute (WCAG
   * 1.4.1 / 3.3.1). The tick is in the same pinned footer as the button, so the
   * reason and the control it explains are always on screen together.
   *
   * It names the ACTION, not a state of mind (Ashley Haveloch-Jones, via the
   * CTO, 2026-09-26: "Please confirm you understand your commitment to
   * continue" was "just weird wording"). WCAG 3.3.1 wants the fix described,
   * and the fix is one tick. "The box" rather than "the box above": the tick
   * and this line swap places between the phone's pinned bar and the desktop
   * rail, so a direction would be wrong on one of them.
   */
  const blockedReasonLine = termsBlocked ? (
    <p id="continue-blocked-reason" style={blockedReasonStyle}>
      {CONTINUE_BLOCKED_REASON}
    </p>
  ) : null;

  const continueToDelivery = (
    <Button
      type="button"
      variant="cta"
      arrow
      disabled={continueBlocked}
      aria-describedby={continueBlocked ? 'continue-blocked-reason' : undefined}
      style={{ marginTop: 'var(--gps-s-3)' }}
      onClick={() =>
        onSelect({
          productKey: option.productKey,
          months: selectedPlan.months,
          medicationName: option.medicationName,
          monthlyPence: selectedPlan.monthlyPence,
        })
      }
    >
      Continue to delivery
    </Button>
  );

  /**
   * THE ONE PLACE THIS SCREEN SAYS WHEN MONEY MOVES (2026-09-26). "Your card
   * won't be charged until a clinician approves your treatment." sat under the
   * button, restating the agreement panel's approval point in other words. It is
   * now the site's own sentence, `NO_CHARGE_UNTIL_APPROVED` ("No charge until
   * your treatment is approved."), in the highlighted box "What happens next"
   * shows it in — the CTO called it important, so it is said once, highlighted,
   * rather than twice in grey. `compact`: the rail's pinned foot is budgeted.
   * In the 20rem rail's pinned foot it drops its decorative shield, which is
   * what wrapped it to a second line there (NoChargeLine's note); everywhere
   * else — the one-column routes, the 24rem rail, a phone — it keeps it.
   */
  const notChargedLine = <NoChargeLine compact style={{ margin: '10px 0 0' }} />;
  const railNotChargedLine = narrowRail ? <NoChargeLine compact icon={false} style={{ margin: '10px 0 0' }} /> : notChargedLine;

  /**
   * THE SAME TRUST CHIPS THE CARD STEP SHOWS (2026-09-26, the CTO: "in the
   * other one you use icons and then chips … align to the same in both
   * screens"). The kit `TrustStrip`, with NO payment chip: its "No charge until
   * approved" would be a third telling of the line just above it, and the card
   * step's hold route drops it for the same reason. Post-eligibility, so the
   * category-level chips are fine here (R28).
   */
  const trustChips = <TrustStrip paymentCue={null} style={{ margin: '8px 0 0' }} />;

  /* The plan-link lane's quiet drop-off line (T-2). Built once so the two-column
     route can place it with the chips — see `railReassurance` below. */
  const holdingBack =
    offerDropOffReason && assessmentId ? <HoldingBackLink clinicSlug={clinicSlug} assessmentId={assessmentId} /> : null;

  // A live order summary of the current selection (medication, term, per-month
  // price AND the term total), shown in the sticky aside beside the choices so
  // the patient always sees what they're committing to. Hidden for the 0-pence
  // launch placeholder, where there is no real total to show.
  //
  // THE CARD STEP'S LOOK (2026-09-26, the CTO: "align to the same in both
  // screens as much as possible"). The card, eyebrow, figure type, plan name and
  // the muted rows are `OrderSummary.tsx`'s — the same pieces `PaymentRail`
  // draws — so the two summaries cannot drift. The term total is the card
  // step's own row (`termTotalLabel`: "N-month total, billed monthly"), and like there a
  // one-month plan has no total row (its total IS the monthly). What is NOT
  // copied is the card step's "Held on your card today" lead: nothing is held
  // on this screen, so the plan's monthly price leads, on the plan row — one
  // line, because this card is the rail's pinned head and every pixel it grows
  // comes out of the agreement list at 1280×720.
  const selectionSummary = selectedPlan.monthlyPence > 0 ? (
    // `gps-jux-panel`: the journey's one white shadow-card (tokens.css, P7).
    <div className="gps-jux-panel" style={orderSummaryCardStyle}>
      <SummaryEyebrow>Order summary</SummaryEyebrow>
      {/* ONE ROW (2026-08-14, CTO): treatment + term on the left, the money on
          the right — "this treatment, this long, this much". Wraps to two lines
          only when the viewport is genuinely too narrow to hold it. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: '4px var(--gps-s-4)',
          flexWrap: 'wrap',
        }}
      >
        <SummaryPlanName inline name={option.medicationName} term={termLabel(selectedPlan.months)} />
        <SummaryFigure as="span" size="row" figure={formatPence(selectedPlan.monthlyPence)} suffix="/month" />
      </div>
      {/* DELIVERY, SAID AS A LINE ITEM (2026-09-24, CTO: "show we do prices
          including delivery and show they don't need to pay anything there").
          The words come from `deliveryCopy.ts` and are "Free" — never a
          struck-through figure: delivery has never been priced to a patient
          anywhere in this estate, so a "was £X" would be a fictitious reference
          price (see that file). Small and muted beneath the money on purpose:
          it is reassurance, not a second price. Inside this block, so it
          vanishes with the rest of the summary when there is no real price. */}
      <div style={summaryRowsStyle}>
        {selectedPlan.months > 1 ? (
          <SummaryRow
            label={termTotalLabel(selectedPlan.months)}
            value={formatPence(selectedPricing.actualPence)}
          />
        ) : null}
        <SummaryRow label={DELIVERY_SUMMARY_ROW.label} value={DELIVERY_SUMMARY_ROW.value} />
      </div>
      {selectedPricing.savePence > 0 ? (
        <div style={{ marginTop: '8px' }}>
          <Badge tone="positive">
            Save {formatPence(selectedPricing.savePence)} over {selectedPlan.months} months
          </Badge>
        </div>
      ) : null}
    </div>
  ) : null;

  // ── Wave 4 §2: the recommendation-filtered TWO-STEP chooser. ──────────────
  if (recommendedKeys) {
    if (step === 'medication') {
      return (
        <div className="gps-plan-breakout">
          <div style={{ maxWidth: '46rem', margin: '0 auto' }}>
            <Title>{PLAN_STEP_TREATMENT}</Title>
            <Help style={{ fontSize: '15.5px' }}>
              Your clinician has recommended {visibleOptions.length === 1 ? 'this treatment' : 'these treatments'} for
              you. Pick one, then choose how long you&rsquo;d like to commit to.
            </Help>
            <div className="gps-treatment-grid">{medicationCards}</div>
            <div style={{ maxWidth: '22rem', marginTop: 'var(--gps-s-4)' }}>
              <Button type="button" variant="cta" arrow onClick={() => setStep('term')}>
                Continue to your commitment
              </Button>
            </div>
            {notChargedLine}
          </div>
        </div>
      );
    }
    return (
      <div className="gps-plan-breakout">
        <div style={{ maxWidth: '46rem', margin: '0 auto' }}>
          <Title>{PLAN_STEP_COMMITMENT}</Title>
          <Help style={{ fontSize: '15.5px' }}>
            {option.medicationName} — recommended by your clinician.{' '}
            {visibleOptions.length > 1 ? (
              <button type="button" onClick={() => setStep('medication')} style={inlineLink}>
                Change treatment
              </button>
            ) : null}
          </Help>
          {/* NO CHIP ROW ABOVE THIS ONE, and no description pointing at one.
              This route has no projection chart, so a second term control here
              would not be "the controller on top of the chart" the CTO asked
              for — it would be the same row twice, stacked. One control,
              announced as one group. */}
          {termGrid()}
          {termEndNote}
          {selectionSummary ? <div style={{ marginTop: 'var(--gps-s-4)' }}>{selectionSummary}</div> : null}
          {termsGate}
          {/* THE STEP'S ACTION ROW — on a phone it sticks to the foot of the
              screen (see the single-page chooser below for the why). The SAME
              button element and handler; only the box around it is new. */}
          <div className="gps-jux-action-row">
            <div style={{ maxWidth: '22rem' }}>{continueToDelivery}</div>
            {blockedReasonLine}
          </div>
          {notChargedLine}
          {trustChips}
          {holdingBack}
        </div>
      </div>
    );
  }

  /* ⚠️ WHERE THE CHIPS AND THE DROP-OFF LINE GO ON THE TWO-COLUMN ROUTE — AND WHY
     IT DEPENDS ON THE RAIL'S WIDTH (review, 2026-09-26, measured in Chrome).
     In the 24rem rail (≥1200px) the two chips are one 31px row and they sit in
     the pinned foot, under the button, like the card step's. In the 20rem rail
     (880–1199px) the same two chips wrap to TWO rows (69.5px) and, with the
     other foot lines wrapping too, the pinned foot reached 361px: at 1024×768
     the agreement's reading window between the pinned head and foot was 10px —
     its own padding — so a patient could tick "I agree" over terms they could
     not see (origin/dev: 76px). So in the 20rem rail they leave the pinned foot
     for the END OF THE LEFT COLUMN, in page flow, where they cost the contract
     nothing. ONE element in either place, never two copies (a second copy would
     be a second list for a screen reader and a second link to Tab through). On a
     phone there is no rail and they stay after the button (`display: contents`). */
  const railReassurance = (
    <>
      {trustChips}
      {holdingBack}
    </>
  );

  // ── No recommendation: the single-page chooser (treatment + term together). ─
  // A wide, two-column layout — the choices on the left, a sticky order summary
  // + CTA on the right. This step used to be a tall single column inside the
  // narrow (34rem) journey frame, which wasted the horizontal space and forced
  // a long scroll; `.gps-plan-breakout` lets JUST this step widen past that
  // frame, and `.gps-plan-layout` splits choices from the summary rail.
  return (
    <div className="gps-plan-breakout">
      {/* ONE COLUMN ON A PHONE, A STICKY SUMMARY RAIL ON THE WEB (2026-08-20,
          CTO: "the final amount and the main button on the right hand side fixed
          pane on web and on mobile to scroll… bring the main CTA to the
          right-hand side top so that the other section will get scrolled if the
          page size is not enough").

          THIS IS THE SCROLL BEHAVIOUR ONLY. Nothing about the DECISION order
          moved: the term control still sits on top of the chart, the chart still
          opens the step, treatment is still step 1 and commitment still step 2,
          and the pane below still runs summary → what you are agreeing to → the
          button. The CTO was explicit that the chart placement and the rest of
          the round-2 work stay exactly as they are.

          A NOTE FOR THE NEXT PERSON READING THE 2026-08-14 COMMENT THAT USED TO
          BE HERE. It argued for stacking on the grounds that a rail level with
          the treatment cards showed the patient a total before they had finished
          choosing. That was answered by the round-2 layout rather than by the
          stacking: the rail no longer sits beside a bare price list, it sits
          beside the estimate and the choices, and it changes under every click —
          which is the point of having it in view. What the stacked version cost
          was worse: on a 1280×720 laptop the button that ends this step sat
          1,700px below the fold, so the patient scrolled a full screen and a
          half after deciding, past everything they had just decided.

          ⚠️ MOBILE HAS NO RAIL, DELIBERATELY. `.gps-plan-layout` is a
          single-column grid until 880px and the rail is not sticky there — a
          pane pinned to the top of a phone screen would eat the fold the chart
          and the cards live in. The sticky rules switch on with the second
          column and never before it, and so do the rail's two halves
          (`.gps-plan-rail-scroll` / `.gps-plan-rail-foot`, 2026-09-04): on a
          phone they are never a split rail, with NO internal scroll of their
          own — the thing being fixed at ≥880px is a nested scroll container the
          patient cannot see, and a phone has none. What a phone DOES get
          (journey polish #4, 2026-09-24) is the journey's sticky action row
          around the one "Continue to delivery" button — see the rail foot. */}
      <div className="gps-plan-layout">
        <div>
          {/* THE TITLE OPENS THE LEFT COLUMN (journey polish #3, 2026-09-24) —
              the SAME elements in the SAME order, one box deeper. At ≥880px it
              lets the summary rail start level with the title instead of below
              the intro, which is what brings "Continue to delivery" above the
              fold of a 1440×900 screen on first paint (it ended 44px below it).
              On a phone nothing moves: one column, title first. */}
          {/* TITLE (2026-08-14, Ali Abbas Jaffari): "Choose your plan" directly above
              a card badged RECOMMENDED reads as a contradiction — the patient was
              told they'd see a recommendation, then asked to choose from scratch.
              "Build your plan" is honest about both halves: we have suggested one,
              and you are still in control of it. */}
          <Title>Build your plan</Title>
          {/* THE COPY NAMES BOTH CHOICES AGAIN (2026-09-04), and deliberately makes
              NO promise about the end of the term. The 2026-08-28 line said "every
              plan runs for 3 months, and you can stop future months at any time",
              and the line before THAT said both choices were changeable "with your
              clinician later" — the first is no longer true and the second never
              was. The exit promise cannot be made up here either: it is false for
              the 1-month term, which ends rather than continuing, and a page-level
              sentence cannot see which term is selected. So the end of the term is
              stated where the term is actually known — in the commitment gate
              (and, for a one-month plan, `termEndNote` under the priced cards) —
              and this line says only what the patient is about to do. */}
          <Help style={{ fontSize: '15.5px', maxWidth: '44rem' }}>
            We&rsquo;ve highlighted the treatment recommended for you &mdash; keep it or pick another,
            then choose how long to commit for.
          </Help>
          {/* THE OUTCOME, BEFORE THE PRICE (2026-08-17, business). The builder
              opened on a number with nothing to weigh it against, so a longer
              term read as "more money for longer" rather than "more time for
              this to work".

              THE TERM CONTROL IS BACK ON TOP OF THE CHART (2026-09-04, CTO —
              restoring the 2026-08-19 brief that the 2026-08-28 term lock had
              removed). It exists so a patient can try three months against
              twelve and watch the figure move; that is the entire reason the
              band was built, and it only works while there is more than one
              term to try. Two ways in, ONE `months`, both routed through
              `chooseTerm` — so the consent reset can no more be bypassed than
              when there was one control, and no arrangement of clicks can leave
              the chart showing a term the patient is not buying.

              ⚠️ IT IS RENDERED HERE, NOT INSIDE WeightProjection, AND THAT IS
              NOT A TIDINESS PREFERENCE. The chart returns null on four separate
              grounds (no figures, one figure, no figure for the SELECTED term,
              every point on the same month) — and while the term selector was
              nested inside it, a catalogue with no seeded projection left the
              patient unable to choose a term AT ALL, with every unit spec green
              (fixed 2026-08-17). A pure UI affordance must never be gated on
              someone else's data. The control is a SIBLING of the chart, and it
              renders whether the chart does or not. */}
          {termControl}

          {/* THE CHART. Every figure in it is `option.projection` — rows owned by
              prices-service — and the only arithmetic done to them here is
              multiplying by this patient's own recorded weight. It stays absent
              for a treatment the catalogue has no signed-off curve for: these
              are efficacy claims about a POM, and the portal must never derive
              one to fill a gap. */}
          <WeightProjection
            points={option.projection ?? []}
            months={selectedPlan.months}
            startKg={startKg}
          />

          <StepHeading n={1}>{PLAN_STEP_TREATMENT}</StepHeading>
          <div className="gps-treatment-grid">{medicationCards}</div>
          <EfficacyNote options={visibleOptions} />

          {/* THE VERB COMES BACK WITH THE CHOICE (2026-09-04). "Commitment"
              still names what this is — the 2026-08-14 note holds, where "plan
              length" was an administrative label — and "Choose" is honest again
              now that there is something to choose. It went away on 2026-08-28
              for exactly the right reason: a heading that says Choose above a
              panel with nothing to choose is the kind of small lie that makes a
              patient distrust the rest of the page. */}
          <StepHeading n={2}>{PLAN_STEP_COMMITMENT}</StepHeading>
          {/* THE SAME CHOICE, SAID OUT LOUD. The sighted patient can see that
              the row above the chart and this one move together; a screen-reader
              user meeting two identically-named sets cannot, and would
              reasonably assume there is a second decision to make here. This
              sentence is the only thing that tells them otherwise — see the
              radiogroup note on `termGrid` for why the two sets are announced
              the way they are. */}
          <span id={TERM_CARDS_SAME_CHOICE_ID} className="gps-sr-only">
            The same choice as How long to commit for above the estimate, restated with prices. Changing
            it here updates the estimate above.
          </span>
          {termGrid(TERM_CARDS_SAME_CHOICE_ID)}
          {termEndNote}

          {/* Answers "why this one, and what will it do" at the point of the
              decision. Mechanism copy is drug-class and keyed on the server's
              form; nothing patient-specific is derived here. */}
          <TreatmentFit form={option.form} bmi={bmi} />
          {narrowRail ? <div style={{ marginTop: 'var(--gps-s-4)' }}>{railReassurance}</div> : null}
        </div>

        {/* THE SUMMARY RAIL. Below 880px this is just the next block down the
            page — no sticky, no column, no internal scroll: the grid is one
            column and the margin comes from its gap. From 880px
            `.gps-plan-aside` pins it to the top of the viewport, clear of the
            emergency ribbon and the green header (both of which are
            `position: sticky` themselves and would otherwise cover the total).

            ⚠️ THE ORDER OF THESE IS A CONSENT ORDER, NOT A VISUAL ONE.
            What you are agreeing to comes BEFORE the button that acts on it, and
            it must keep coming before it — in the DOM, which is what a screen
            reader and the Tab key follow. Moving the CTA above the commitment
            gate to "get the button higher up the rail" would put the action in
            front of the terms it depends on, for a patient committing to months
            of a prescription medicine and the money that goes with it. The split
            below preserves that order exactly: the bullets are in the scrolling
            half, the tick is the first thing in the pinned half, the button
            follows it.

            ⚠️ THE CTA IS NO LONGER AT THE BOTTOM OF AN INNER SCROLL
            (2026-09-04, CTO: "the two columns scroll independently, and the page
            scrolls independently. It's difficult to get to the CTA unless you
            know your way around… 'what you're agreeing to' is a bit too lengthy,
            which covers the CTA").

            THE MECHANISM, because the fix only makes sense against it. The pane
            is ~740px on the launch catalogue against ~575px of usable height at
            1280×720, so it is capped and scrolls inside itself — that cap is
            NOT the bug and must not be removed (a sticky pane taller than the
            viewport strands its own bottom, which is worse). The bug was WHAT
            was at the bottom of that inner scroll: the button, underneath the
            long agreement list. A patient had to first work out that the rail
            was its own scroll container — with the page and the left column both
            scrolling too — and then scroll inside it. Nothing on screen said so.

            So the rail is a flex column in THREE parts, and which part is which
            comes straight from the two CTO briefs it has to satisfy at once.
            2026-08-20 asked for "the final amount and the main button on the
            right hand side fixed pane"; 2026-09-04 says the button is
            unreachable. Both hold if the ONLY thing that scrolls is the thing
            that is genuinely long: `.gps-plan-rail-head` pins the order total at
            the top, `.gps-plan-rail-scroll` takes the agreement bullets and the
            overflow, and `.gps-plan-rail-foot` pins the tick, the button and
            the one "No charge until your treatment is approved." line at the
            foot — with the trust chips under them in the 24rem rail (2026-09-26:
            the second-thoughts call-back link that sat there went — the journey
            footer's offer is this screen's one call-back route, see
            `HoldingBackLink`; in the 20rem rail the chips go to the left column,
            see `railReassurance`).

            MEASURED IN A BROWSER AT 1280×720, which is the WORST case and the
            one the CTO was looking at: a 579px pane splits 188px of pinned total
            + 104px of scrolling agreement + 275px of pinned footer, with the CTA
            landing at y=551 — on screen from first paint, where it used to sit
            below ~570px of inner scroll. The reading window is genuinely tight
            at that height and that is the deliberate trade: BOTH pinned parts
            were asked for (the total on 2026-08-20, the button on 2026-09-04),
            and a clipped list that says it is clipped beats a button nobody can
            find. It relaxes on its own with height — ~280px of reading at a
            900px viewport, nothing scrolls at all on a 1080p one. If the footer
            grows, this is the number it is spending.

            ⚠️ AND THE 20rem RAIL (880–1199px) IS ITS OWN WORST CASE — MEASURE IT
            TOO (review, 2026-09-26). Every line wraps sooner there. The dedupe
            that added the no-charge box, the chips and the total row was measured
            at 1280×720 and 1440×900 only, and at 1024×768 its reading window was
            10px — none of the agreement on screen. Now (Chrome, :8796 stub):
            1024×768 154px, 960×720 106px, 880×800 186px, 1180×820 206px,
            1280×720 92px (origin/dev: 76 / 28 / 108 / 128 / 67). The budget
            rules are pinned in `checkout/planRailBudget.spec.tsx`.

            ⚠️ AND THE SCROLLING HALF SAYS SO WHEN IT IS CUT OFF. A clipped
            agreement list that looks like a finished one is worse than a
            hard-to-find button: the patient ticks a box agreeing to bullets they
            never saw. `moreBelow` is measured, not assumed, and drives a line in
            the footer AND a fade at the cut edge — text first, because a fade
            alone is a colour-only cue (WCAG 1.4.1).

            MOBILE GETS NO RAIL. Below 880px none of the rail rules apply: one
            column, no rail, no internal scroll. The trap being fixed here is a
            NESTED scroll nobody can see; a phone has none.

            ⚠️ BUT ON A PHONE THE CTA STICKS (journey polish #4, CTO 2026-09-24:
            "on mobile, having to scroll is normal with the button fixed towards
            the bottom if there is a CTA"). It sat ~3,400px down a 3,560px page.
            The button and the line saying why it is off are wrapped in the
            journey's action row (`.gps-jux-action-row`), which tokens.css makes
            `position: sticky; bottom: 0` below 880px — the SAME button element
            and handler, never a second tracked button. It is sticky, not fixed,
            so it settles into its slot straight after the tick at the end and
            covers nothing there; and the reason line travels WITH the button,
            so a disabled button is never shown without its condition in words.
            The tick itself stays in the flow above it, in consent order. For the
            row to stick across the whole screen, the rail's two wrappers step
            aside on a phone (`display: contents`, P7) so the row's containing
            block is the plan layout, not the short foot. */}
        <div className="gps-plan-aside">
          {/* THE TOTAL STAYS PUT (2026-08-20, CTO). It is the reason the rail
              exists, and letting it scroll away with the bullets would trade one
              CTO decision for the other. */}
          <div className="gps-plan-rail-head">{selectionSummary}</div>

          <div
            className="gps-plan-rail-scroll"
            ref={railScroll.ref}
            /* The fade at the cut edge (tokens.css). An ATTRIBUTE rather than an
               inline style so the whole rule — including the `-webkit-` twin —
               stays with the rest of the rail's CSS, and so the specs can read
               one answer for "does this look clipped". */
            data-more-below={railScroll.moreBelow ? 'true' : 'false'}
            /* KEYBOARD-OPERABLE ONLY WHEN IT ACTUALLY SCROLLS (WCAG 2.1.1). A
               scrollable region no keyboard can reach is a trap; a tab stop on
               a plain div that fits its content is noise on the money path. It
               is measured, so it is neither by guess. */
            {...(railScroll.scrollable
              ? { tabIndex: 0, role: 'group', 'aria-label': 'What you’re agreeing to' }
              : {})}
          >
            {railAgreement}
          </div>

          <div className="gps-plan-rail-foot">
            {railScroll.moreBelow ? (
              /* SAID IN WORDS, not by the fade above it. `role="status"` rather
                 than `alert`: it is orientation, not an error — and it is
                 announced when it appears because it appears precisely when part
                 of the contract is off-screen. */
              <p role="status" style={railMoreToReadStyle}>
                There&rsquo;s more to read &mdash; scroll the panel above.
              </p>
            ) : null}
            {/* THE GAP THE OLD ONE-PANEL GATE USED TO CARRY. `CommitmentTerms`
                had `marginTop` on its own box; the tick on its own has none, so
                on a phone — where the foot is a plain block in normal flow — it
                would sit flush against the agreement panel above it. The class
                zeroes itself at ≥880px, where the foot's own rule already
                separates the two. */}
            <div className="gps-plan-rail-tick">{railTick}</div>
            {/* The step's action row: sticky at the foot of a phone screen, an
                ordinary block in the pinned foot at ≥880px (see above). */}
            <div className="gps-jux-action-row">
              {continueToDelivery}
              {blockedReasonLine}
            </div>
            {railNotChargedLine}
            {/* In the 20rem rail these are at the end of the left column instead
                — see `railReassurance`. */}
            {narrowRail ? null : railReassurance}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The term control that sits ABOVE the projection chart (2026-08-19, CTO;
 * deleted by the 2026-08-28 term lock and restored 2026-09-04).
 *
 * NOT A CHART FILTER, and not a second source of truth. It writes the SAME
 * `months` the commitment cards at step 2 write, through the SAME `chooseTerm`,
 * which is why "selecting here pre-populates question 2" and "they can change
 * it again down there" are one behaviour rather than two features. `onChoose`
 * is the whole interface: this component holds no term state of its own, so
 * there is nothing here that can disagree with the cards below.
 *
 * ⚠️ ROUTE EVERY PATH THROUGH `onChoose`. `chooseTerm` also clears the
 * commitment tick — a patient agreed to a specific term at a specific figure,
 * and that agreement does not carry to a different one. Reaching for `setMonths`
 * from inside here to "just move the chart" would record consent to a plan
 * nobody consented to, silently, about money.
 *
 * ⚠️ AND IT LIVES IN THIS FILE, NOT IN WeightProjection.tsx. That component
 * returns null when the catalogue has no signed-off curve; a term selector
 * defined and rendered inside it vanished with the chart and left the patient
 * unable to choose a term at all (2026-08-17). Keep the affordance and the data
 * apart.
 *
 * WHY IT IS THE PAGE'S RADIOGROUP, while the priced cards below stay a labelled
 * `group`: see the note on `termGrid`. Claiming the role means honouring it, so
 * this carries the full radio keyboard contract — a roving tabindex (the set is
 * ONE Tab stop, not four) and arrow keys that move the selection within it.
 * Home/End jump to the shortest and longest terms. That is what a screen reader
 * has just promised the user when it says "radio group".
 *
 * SELECTION IS NOT CARRIED BY COLOUR (WCAG 1.4.1). The chosen term lifts onto
 * its own raised white surface, out of the sunken track, and its label goes
 * bold — two cues that survive any colour vision, on top of `aria-checked`. No
 * tick glyph, deliberately: measured at 375px the four chips fill the track
 * with nothing to spare, and an icon plus its gap is exactly enough to push
 * "12 months" onto a second row. The tick the priced cards carry has room; this
 * one would cost the row its shape to say what the raised surface already says.
 */
function TermRadioGroup({
  terms,
  months,
  onChoose,
}: {
  /** The PURCHASABLE terms, in catalogue order. */
  terms: number[];
  months: number;
  /** `chooseTerm` — never a bare setter (see above). */
  onChoose: (months: number) => void;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // The Tab stop. Falls back to the FIRST term when the selection is somehow
  // not in the list: ARIA's answer for a radiogroup with nothing checked, and
  // it keeps the set keyboard-reachable rather than stranding it at tabindex -1.
  const activeIndex = Math.max(0, terms.indexOf(months));

  /** Move the selection by `delta`, wrapping — and take focus with it, because
   *  in a radiogroup the focused radio IS the selected one. */
  const step = (delta: number) => {
    if (terms.length === 0) return;
    const next = (activeIndex + delta + terms.length) % terms.length;
    onChoose(terms[next]);
    refs.current[next]?.focus();
  };

  const jumpTo = (index: number) => {
    if (terms.length === 0) return;
    onChoose(terms[index]);
    refs.current[index]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      // Both axes on purpose: the row wraps to two lines on a narrow phone, so
      // "next" is Down as often as it is Right.
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        step(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        step(-1);
        break;
      case 'Home':
        event.preventDefault();
        jumpTo(0);
        break;
      case 'End':
        event.preventDefault();
        jumpTo(terms.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ marginBottom: 'var(--gps-s-4)' }}>
      {/* A REAL VISIBLE LABEL, referenced by `aria-labelledby` rather than
          duplicated into an `aria-label`: the accessible name is then the text
          on screen, so a speech-input user can say what they can see (WCAG
          2.5.3) and the two can never drift apart in a copy edit. */}
      <p
        id={TERM_CONTROL_LABEL_ID}
        style={{ margin: '0 0 8px', fontSize: '13.5px', fontWeight: 700, color: 'var(--gps-text)' }}
      >
        How long to commit for
      </p>
      {/* THE HINT EXPLAINS THE DUPLICATION TO EVERYONE. A patient who meets the
          same terms twice needs to know the second row is not a second
          decision — sighted readers get this sentence, screen-reader users get
          it here AND from the description on the cards below. */}
      <p
        id={TERM_CONTROL_HINT_ID}
        style={{ margin: '0 0 10px', fontSize: '13px', lineHeight: 1.45, color: 'var(--gps-muted)' }}
      >
        Try a length to see the estimate. It carries down to step 2, where you can change it.
      </p>
      <div
        role="radiogroup"
        aria-labelledby={TERM_CONTROL_LABEL_ID}
        aria-describedby={TERM_CONTROL_HINT_ID}
        onKeyDown={onKeyDown}
        style={{
          display: 'flex',
          // WRAPS RATHER THAN SQUASHES — the safety net, not the normal case.
          // The four terms fit one row down to 320px as measured; they stop
          // fitting under a reader's enlarged text, and then a second row is
          // far better than four chips clipping their own labels.
          flexWrap: 'wrap',
          gap: '4px',
          background: 'var(--gps-surface-2)',
          border: '1px solid var(--gps-border)',
          borderRadius: 'var(--gps-r-pill)',
          padding: '4px',
        }}
      >
        {terms.map((term, index) => {
          const on = term === months;
          return (
            <button
              key={term}
              type="button"
              role="radio"
              aria-checked={on}
              ref={(el) => {
                refs.current[index] = el;
              }}
              // ROVING TABINDEX: one Tab stop for the whole set, arrows within
              // it. Four separate Tab stops would make the keyboard user pass
              // through every term to reach the treatments below.
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => onChoose(term)}
              style={{
                flex: '1 1 auto',
                border: 0,
                background: on ? 'var(--gps-surface)' : 'transparent',
                color: on ? 'var(--gps-text)' : 'var(--gps-muted)',
                fontWeight: on ? 700 : 600,
                fontSize: '13.5px',
                // Tight horizontal padding buys the four terms a single row at
                // 320px — the narrowest width this journey still has to work at.
                padding: '9px 4px',
                borderRadius: 'var(--gps-r-pill)',
                cursor: 'pointer',
                boxShadow: on ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                // 44px minimum touch target (WCAG 2.5.5 / 2.1 AA practice) —
                // this row is the first thing a thumb meets on the page.
                minHeight: '44px',
                whiteSpace: 'nowrap',
              }}
            >
              {term} {term === 1 ? 'month' : 'months'}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A numbered section heading — the "1 · Your treatment" / "2 · Your commitment"
 * step cue that gives the single-page chooser a clear two-step reading order. */
function StepHeading({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    // A real <h2> under the screen's <h1> Title so the two steps are navigable
    // by heading (WCAG — #79 review); the numbered chip + styling keep the look.
    <h2
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: 'var(--gps-s-6) 0 var(--gps-s-4)',
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--gps-text)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '24px',
          height: '24px',
          boxSizing: 'border-box',
          borderRadius: 'var(--gps-r-pill)',
          background: 'var(--gps-primary-light)',
          // A soft light-green outline gives the numbered chip a clear edge
          // without the heavy full-strength green ring.
          border: '1px solid var(--gps-primary-tint-strong)',
          color: 'var(--gps-green-text)',
          display: 'grid',
          placeItems: 'center',
          fontSize: '13px',
          fontWeight: 700,
          flex: 'none',
        }}
      >
        {n}
      </span>
      {children}
    </h2>
  );
}

/**
 * A SOLID-green pill (white text) for the "Recommended" / "Best value" cues.
 * The default Badge (positive tone) is primary-LIGHT on a light tint — which
 * vanishes when the cue sits on a SELECTED card whose background is that same
 * tint (2026-07-25). A solid fill reads clearly on both the white (unselected)
 * and the light-green (selected) card surfaces.
 */
function SolidPill({ children, tone = 'primary' }: { children: React.ReactNode; tone?: 'primary' | 'accent' }) {
  return (
    <span
      style={{
        display: 'inline-block',
        // Two distinct fills so "Most popular" and "Best value" are not the same
        // badge in two places — the patient must be able to tell them apart
        // without reading. Both carry their meaning in WORDS as well as colour.
        background: tone === 'accent' ? 'var(--gps-accent, #c2410c)' : 'var(--gps-primary)',
        color: 'var(--gps-on-accent)',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        padding: '3px 9px',
        borderRadius: 'var(--gps-r-pill)',
      }}
    >
      {children}
    </span>
  );
}

/**
 * A treatment card (C2.1 UX pass, 2026-07-24; COMPACTED 2026-09-26): a raised
 * card with a strong selected state (green fill + 2px ring + check), the form
 * mark and the name on ONE row with the "Recommended" pill as a tab on the
 * card's top edge, and the efficacy band and the "from £X a day" price on the
 * row under it.
 *
 * SHORTER, NOT SMALLER (2026-09-26, the CTO on a phone: "the medication cards
 * where we show the prices are a bit too large, you have to scroll a lot …
 * reduce the card height, even on web"). The card was four stacked bands — a
 * badge row, the name, the result, a two-line price — and on a phone each
 * treatment took ~200–220px (measured at 375×812 in Chrome; ~285px at
 * 1440×900, where the side-by-side reserves added room for a badge and three
 * lines of name on every card). Nothing it SAID went: the badge, the name, the
 * form mark, the "−N% avg. loss over 12 months" band (its time frame is not
 * optional, 2026-08-18), the "from £X a day" price and the selected tick are all
 * still here. What went is the vertical rhythm: the badge moved onto the card's
 * edge, the "from" label runs into its figure, and the band and the price share a row
 * where the width allows it (they wrap onto two lines, band first, where it does
 * not). The measured heights are in the CHANGELOG entry.
 *
 * The price is one text node for the figure (a test asserts the exact string).
 * Selection is shown three ways — the ring, the tint and the tick — never by
 * colour alone; the whole card is ONE `aria-pressed` button, as before.
 */
function MedicationCard({
  option,
  selected,
  fromPence,
  mostEffective,
  onClick,
}: {
  option: PlanOption;
  selected: boolean;
  /** Cheapest monthly price across this option's terms, in integer pence. */
  fromPence: number;
  /** Carries the strongest-published-figure star. Exactly one card, or none. */
  mostEffective: boolean;
  onClick: () => void;
}) {
  /* ONE CLAIM, ONE PILL (2026-08-20). prices-service flags `recommended` on the
     treatment with the highest published efficacy figure (CTO decision), so the
     two pills are usually the SAME fact, and stacking them reads as two
     independent endorsements of one card. "Recommended" wins because it is the
     one the preselection follows, so the badge and the preselected card explain
     each other. "Most effective" still shows on its own whenever the two
     genuinely differ, which is exactly when a patient needs to be told they are
     looking at two different things. */
  const badge = option.recommended ? (
    <SolidPill>Recommended</SolidPill>
  ) : mostEffective ? (
    <SolidPill tone="accent">
      <span aria-hidden="true">★ </span>Most effective
    </SolidPill>
  ) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
        font: 'inherit',
        color: 'var(--gps-text)',
        cursor: 'pointer',
        background: selected ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
        // Unselected (incl. the non-recommended treatments) rest on a THIN LIGHT
        // border; selection carries the weight via the 2px green ring + tint + tick
        // (border-box, so the 1px→2px swap doesn't shift the card).
        border: selected ? '2px solid var(--gps-primary)' : '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        padding: '14px',
        // Content from the TOP. A <button> centres its content vertically, so a
        // card stretched to its row's height (the grid gives both cards one
        // height) would float its rows in the middle.
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      {/* THE BADGE SITS ON THE CARD'S TOP EDGE — a tab, taking NO row of its
          own (2026-09-26). Inline beside the name it squeezed the name onto two
          lines in the laptop's two-up grid; on a row of its own it was the
          52px band the CTO's "too large" was about. Straddling the border it
          costs nothing and still reads as belonging to this card. Left-hand,
          so it never meets the selected tick; a leaf element, so its position
          touches no wrapper of journey content. */}
      {badge ? <span style={{ position: 'absolute', top: '-10px', left: '14px', lineHeight: 1 }}>{badge}</span> : null}
      {/* Selected reads from the green border + tint fill; a bare corner tick
          keeps it from being colour-alone — no radio/checkbox circle (2026-07-25). */}
      {selected ? (
        <span
          aria-hidden="true"
          style={{ position: 'absolute', top: '12px', right: '12px', display: 'inline-flex', color: 'var(--gps-primary)' }}
        >
          <CheckIcon size={18} />
        </span>
      ) : null}

      {/* THE FORM MARK AND THE NAME, ON ONE ROW (the badge is on the edge,
          above). The name wraps inside its own box (`minWidth: 0`). The
          side-by-side reserve (`.gps-tc-title`, tokens.css) keeps two cards'
          second rows level where one name takes two lines and the other one —
          it switches off in a single column, where a reserve is only dead
          space. (No example names here: R28's source scan bans POM brand and
          delivery-form words in this tree, and a comment counts.) */}
      <span
        className="gps-tc-title"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '17px',
          fontWeight: 700,
          lineHeight: 1.25,
          paddingRight: selected ? '24px' : 0,
        }}
      >
        <TreatmentFormIcon form={option.form} size={16} />
        <span style={{ minWidth: 0 }}>{option.medicationName}</span>
      </span>

      {/* THE RESULT AND THE PRICE, SIDE BY SIDE WHERE THEY FIT. Both are
          SERVER-SUPPLIED and either may be absent (a treatment with no approved
          figure shows no band; the 0-pence placeholder shows no price) — never
          an estimate or a neighbouring medication's number: this is an efficacy
          claim about a prescription-only medicine. The band keeps its own tinted
          shape and the price stays plain dark text, so the eye reads "what it
          does" and "what it costs" as two facts (2026-08-14, CTO). */}
      <span
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px 8px',
          marginTop: '10px',
        }}
      >
        {typeof option.expectedWeightLossPercent === 'number' ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '4px',
              background: 'var(--gps-primary-light)',
              border: '1px solid var(--gps-primary-tint-strong)',
              borderRadius: 'var(--gps-r-md)',
              padding: '3px 7px',
              // ONE LINE, ALWAYS (2026-08-14, CTO): "-21%" is wider than "-8%",
              // and a wrapped label would make one card's band taller than the rest.
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                fontSize: '17px',
                fontWeight: 600,
                fontFamily: 'var(--gps-font-display)',
                lineHeight: 1,
                color: 'var(--gps-green-text)',
              }}
            >
              &minus;{option.expectedWeightLossPercent}%
            </span>
            {/* THE TIME FRAME IS NOT OPTIONAL (2026-08-18). This figure is the
                12-MONTH whole-course average, while the projection band above
                shows the SELECTED term — naming the period is what makes the two
                efficacy numbers on one screen agree. */}
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gps-green-text)' }}>
              avg. loss over 12 months
            </span>
          </span>
        ) : null}

        {/* PRICE — plain dark text, deliberately NOT green. Per-day leads (a
            patient compares £5.57 against daily spending, not against rent);
            "from" runs into it because it is the cheapest of the terms. The
            monthly figure lives on the commitment cards and the order summary
            (2026-08-14, CTO), never hidden — only not repeated here. */}
        {fromPence > 0 ? (
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '3px', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '12px', color: 'var(--gps-muted)', fontWeight: 600 }}>from</span>
            <span style={{ fontSize: '18px', fontWeight: 600, fontFamily: 'var(--gps-font-display)', color: 'var(--gps-text)' }}>
              {formatPence(perDayPence(fromPence))}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--gps-muted)', fontWeight: 600 }}>a day</span>
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * TermCard — one plan-length option as a compact, selectable card for the term
 * row (C2.1 UX pass, 2026-07-25; deleted by the 2026-08-28 term lock and
 * restored 2026-09-04; COMPACTED 2026-09-26). The month label is the FIRST text
 * node (tests locate a term by that prefix); the shared {@link PlanTermCard}
 * renders the price block so patient + admin previews never drift. Selection
 * shows three ways — the green ring, the tint fill and the tick — so it is never
 * carried by colour alone.
 *
 * SHORTER (2026-09-26, the CTO: "reduce the card height, even on web"). Tighter
 * padding and a price block with no reserved blank rows (see PlanTermCard); on
 * a laptop the four terms now sit in ONE row (`.gps-term-grid`, tokens.css),
 * and on a phone in two tight columns with each price on one line.
 *
 * NO BADGE ROW. "Most popular" and "Best value" were deleted with the term
 * choice on 2026-08-28 and did NOT come back with it; the reason is set out on
 * {@link DEFAULT_MONTHS}.
 */
function TermCard({
  plan,
  pricing,
  selected,
  onClick,
}: {
  plan: PricePlan;
  pricing: TermPricing;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
        font: 'inherit',
        color: 'var(--gps-text)',
        cursor: 'pointer',
        background: selected ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
        // Match the treatment cards: thin light resting border, 2px green ring when selected.
        border: selected ? '2px solid var(--gps-primary)' : '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        padding: '12px 10px',
        // The 44px target floor (WCAG 2.5.5); the content is taller anyway.
        minHeight: '44px',
        // Content from the TOP (a <button> centres it): the grid gives a row's
        // cards one height, and the 1-month card, with no saving row, would
        // otherwise float its label and price below its neighbours'.
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      {/* Bare tick, absolutely positioned top-right to match the treatment card
          — no radio/checkbox circle (2026-07-25). */}
      {selected ? (
        <span
          aria-hidden="true"
          style={{ position: 'absolute', top: '11px', right: '9px', display: 'inline-flex', color: 'var(--gps-primary)' }}
        >
          <CheckIcon size={16} />
        </span>
      ) : null}
      <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, lineHeight: 1.3, paddingRight: selected ? '20px' : 0 }}>
        {plan.months} month{plan.months === 1 ? '' : 's'}
      </span>
      <span style={{ display: 'block', marginTop: '4px' }}>
        <PlanTermCard plan={plan} pricing={pricing} />
      </span>
    </button>
  );
}

/**
 * The quiet "what's holding you back?" line (T-2, 2026-09-11) — the plan-link
 * lane only.
 *
 * WHY: the plan step is where a patient meets the price, and it is the most
 * likely drop-off on the whole journey. An approved-but-unpaid patient who
 * arrived on their plan link and hesitates can tell us why, instead of leaving
 * with no signal that price (or anything else) was the reason. Only that lane
 * passes an assessment: the server would silently discard an answer from anyone
 * else, so offering it to them would be a thank-you for nothing.
 *
 * ⚠️ THE "SPEAK TO A CLINICIAN FIRST" LINK THAT SHARED THIS LINE IS GONE
 * (2026-09-26, the CTO: "we are saying the same thing over and over"). It
 * opened the standalone call-back page in a new tab, a few centimetres above
 * the journey footer's own offer — "Rather speak with a licensed clinician?
 * Request a free call back" (`journey/JourneyCallbackOffer`) — which JourneyPage
 * renders under EVERY checkout stage (`offerBelowStage`), so it is on every
 * lane this screen is reachable from: the single-page chooser, the
 * recommendation route and the plan-link lane (JourneyPage is the only place
 * CheckoutFlow, and so this screen, is mounted). The footer's offer is the
 * better of the two: it opens the SAME call-back form IN PLACE, keeps the
 * journey's lead on the ticket, and never leaves a half-built plan in another
 * tab. `journey/planScreenCallback.spec.tsx` walks all three lanes and fails if
 * the footer offer is missing from any of them.
 *
 * DELIBERATELY UNDERPLAYED: a plain text link under the CTA, not a second
 * button. It must never compete with Continue. It opens in a NEW TAB so the
 * half-built plan stays alive in this one (the drop-off page is its own route).
 */
function HoldingBackLink({ clinicSlug, assessmentId }: { clinicSlug: string; assessmentId: string }) {
  return (
    <p style={{ margin: 'var(--gps-s-3) 0 0', textAlign: 'center', fontSize: '13.5px', lineHeight: 1.45 }}>
      <span style={{ color: 'var(--gps-muted)' }}>Not sure yet? </span>
      <a
        // The id rides the FRAGMENT, never the query — see dropOffPagePath. A
        // raw anchor opening a NEW tab, so it carries the served prefix itself
        // (config.ts BASENAME) — react-router only prefixes a <Link>.
        href={withBasename(dropOffPagePath(clinicSlug, assessmentId))}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'var(--gps-link)' }}
      >
        Tell us what&rsquo;s holding you back
      </a>
      <span className="gps-sr-only"> (opens in a new tab — your plan is kept)</span>
    </p>
  );
}

/**
 * The efficacy caveat that has to sit with the numbers.
 *
 * WHY (2026-08-14, CTO): the cards now carry an average weight-loss figure, and
 * an average presented bare reads as a promise. It is not one — the trial
 * figures come from supervised studies alongside diet and exercise, individual
 * results vary widely, and some patients lose little or stop for clinical
 * reasons. Stating that plainly is both the honest thing and the thing that
 * protects the clinic: an unqualified efficacy claim about a prescription-only
 * medicine is a regulatory exposure, not a marketing flourish.
 *
 * It renders ONLY when at least one visible card is actually showing a figure —
 * a disclaimer with nothing to disclaim is noise that trains people to skip the
 * next one.
 *
 * ⚠️ The wording is a DRAFT pending clinical + legal sign-off, like the
 * commitment terms in CommitmentTerms.tsx. Do not ship it unreviewed.
 *
 * SHORTER SINCE 2026-09-26 (Ashley Haveloch-Jones, via the CTO: "too much
 * text"; "don't lose the messaging but condense it"). Every qualifier the long
 * version carried is still here, in about half the words: trial averages, taken
 * alongside a reduced-calorie diet and more activity; averages, not a promise;
 * some people lose more, some less, and some have to stop; the clinician talks
 * them through what is realistic. The sign-off note above still applies to it.
 */
function EfficacyNote({ options }: { options: PlanOption[] }) {
  if (!options.some((o) => typeof o.expectedWeightLossPercent === 'number')) return null;
  return (
    <p
      style={{
        margin: 'var(--gps-s-3) 0 0',
        fontSize: '13px',
        lineHeight: 1.55,
        color: 'var(--gps-muted)',
      }}
    >
      <span aria-hidden="true">★ </span>Average results from clinical trials, alongside a reduced-calorie diet
      and more activity. <strong style={{ color: 'var(--gps-text)' }}>Averages, not a promise</strong> — some
      people lose more, some less, and some have to stop. Your clinician will talk you through what’s
      realistic for you.
    </p>
  );
}

/**
 * "Is there more of this panel below the fold, right now?" — measured, for the
 * plan rail's scrolling half.
 *
 * WHY MEASURED AND NOT ASSUMED (2026-09-04). The rail overflows on a 1280×720
 * laptop and fits on a tall monitor; the agreement list is four bullets on a
 * multi-month term and two on a one-month one; and the CTO's complaint was
 * precisely that the cut edge is invisible. A hint hard-coded on would lie to
 * the patient with room to spare, and a hint hard-coded off is the bug. Both
 * answers depend on the actual box, so the actual box is what is read.
 *
 * TWO ANSWERS, NOT ONE, because they gate different things:
 *   · `scrollable` — the region can scroll at all. It decides the tab stop
 *     (WCAG 2.1.1: a scrollable region has to be operable by keyboard), and it
 *     stays true once the patient has scrolled to the end.
 *   · `moreBelow` — there is content past the bottom edge THIS INSTANT. It
 *     drives the "more to read" line and the fade, and it goes away when the
 *     patient reaches the end, because at that point the hint would be false.
 *
 * ⚠️ NO OSCILLATION, AND THAT IS WHY THE HINT IS ALLOWED TO CHANGE THE HEIGHT.
 * Showing the line shortens the scrolling half, which can only make `moreBelow`
 * MORE true; hiding it lengthens the half, and it is only hidden when the
 * content already fits or the patient is at the end. Neither direction can flip
 * the other back.
 *
 * ⚠️ jsdom HAS NO LAYOUT, so every measurement is 0 there and both answers are
 * false — the specs that exercise this stub the element's own metrics rather
 * than pretending a renderer exists.
 */
function useRailOverflow() {
  /* A CALLBACK REF, NOT A `useRef` — AND THAT IS A BUG FIX, NOT A STYLE CHOICE.
     This component returns early while the catalogue loads, so the region does
     not exist on the first render. A `useRef` read from an effect with stable
     deps is null at that point and the effect never runs again once the real
     node arrives: the listeners are never attached and the hint never appears.
     The node itself is state, so its arrival re-runs everything that needs it. */
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [state, setState] = useState({ scrollable: false, moreBelow: false });

  const measure = useCallback(() => {
    if (!node) return;
    // 1px of slack for sub-pixel rounding; 4px before claiming "more below", so
    // a rounding remainder never renders a hint pointing at nothing.
    const scrollable = node.scrollHeight - node.clientHeight > 1;
    const moreBelow = scrollable && node.scrollHeight - node.scrollTop - node.clientHeight > 4;
    setState((prev) =>
      prev.scrollable === scrollable && prev.moreBelow === moreBelow ? prev : { scrollable, moreBelow },
    );
  }, [node]);

  /* After EVERY render: the content changes with the selected term (a one-month
     plan has two bullets, the others have three) and a ResizeObserver on the box
     never fires for that — the box is the same size, its contents are not. */
  useEffect(measure);

  useEffect(() => {
    if (!node) return;
    node.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    /* Guarded the same way the emergency ribbon guards its own (ui/chrome.tsx):
       jsdom does not implement ResizeObserver, and this must not be the thing
       that stops the plan step rendering in a test. */
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(node);
    return () => {
      node.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [node, measure]);

  return { ref: setNode, ...state };
}

/**
 * THE 20rem RAIL: two columns, before `.gps-plan-layout` widens the rail to 24rem
 * at 1200px (tokens.css). The one width band where the rail's pinned foot cannot
 * afford the trust chips — see `railReassurance` in {@link PlanSelection}.
 */
export const NARROW_PLAN_RAIL_QUERY = '(min-width: 880px) and (max-width: 1199.98px)';

/**
 * True while the viewport is in {@link NARROW_PLAN_RAIL_QUERY}. False with no
 * `matchMedia` (jsdom, an old browser): the chips then stay in the foot, which is
 * where every other width has them — the fallback is the layout that was always
 * there, never a missing row.
 */
function useNarrowPlanRail(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW_PLAN_RAIL_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(NARROW_PLAN_RAIL_QUERY);
    const onChange = () => setNarrow(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return narrow;
}

/** The "there's more to read" line, at the top of the rail's pinned footer —
 *  directly under the cut edge it is describing. */
const railMoreToReadStyle: React.CSSProperties = {
  margin: '0 0 var(--gps-s-2)',
  fontSize: '12.5px',
  fontWeight: 600,
  lineHeight: 1.4,
  color: 'var(--gps-muted)',
};

/**
 * The one-month plan's end-of-term line under the priced cards. Full body colour
 * rather than muted grey: it is the fact that the plan stops, not fine print,
 * and greying it would be the quiet way of not saying it.
 */
const termEndNoteStyle: React.CSSProperties = {
  margin: 'var(--gps-s-3) 0 0',
  fontSize: '13.5px',
  lineHeight: 1.5,
  color: 'var(--gps-text)',
};

/** The muted "why the button is off" line under the CTA — one style, both reasons. */
const blockedReasonStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--gps-muted)',
  marginTop: '8px',
  textAlign: 'center',
  lineHeight: 1.45,
};

const inlineLink: React.CSSProperties = {
  background: 'none',
  border: 0,
  color: 'var(--gps-link)',
  font: 'inherit',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};
